import React, { useState } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectFocusLists,
  selectFocusOverrides,
  setFocusLists,
  setFocusOverrides,
  setSaveError,
} from '../../../../store/slices/peopleConfigSlice';
import { focusToRedux } from '../../../../store/thunks/peopleConfigThunks';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import AdminButton from '../../../Admin/components/AdminButton';
import AdminStatusBadge from '../../../Admin/components/AdminStatusBadge';
import { AdminColors } from '../../../Admin/constants/adminColors';
import FontFamily from '../../../../constants/fonts';
import ConfigCard from '../components/ConfigCard';
import ConfigGhostButton from '../components/ConfigGhostButton';
import ConfigConfirmModal from '../components/ConfigConfirmModal';
import ConfigNameDrawer from '../components/ConfigNameDrawer';
import ConfigTable from '../components/ConfigTable';
import OrgDeptPicker from '../components/OrgDeptPicker';
import TabBody from '../components/TabBody';
import { usePeopleConfig } from '../PeopleConfigContext';
import { FocusArea } from '../configTypes';
import { getApiErrorMessage, locationKey } from '../configUtils';
import { useAppLocationNames } from '../useAppLocations';
import FocusOverrideDrawer from './FocusOverrideDrawer';
import {
  ApiFocusAreaItem,
  createFocusArea,
  deleteFocusAreaOverride,
  saveFocusAreaOverride,
  updateFocusArea,
} from '../../../../api/peopleConfigApi';

type DrawerState =
  | { kind: 'name'; focus: FocusArea | null }
  | { kind: 'override'; loc?: string }
  | null;

/** Maps one backend FocusAreaItemDTO to the Redux `FocusArea` shape. */
const toFocusArea = (item: ApiFocusAreaItem): FocusArea => ({
  id: item.id,
  name: item.name,
  open: item.openCount,
  enabled: item.enabled,
  ...(item.locked ? { locked: true } : {}),
});

/**
 * Process Focus Areas for coaching that does not come from an observation behavior.
 * The national list is managed per org and department; a location override turns specific
 * national focus areas off at that location. "Other" cannot be disabled.
 */
const FocusAreasTab: React.FC = () => {
  const ctx = usePeopleConfig();
  const { org, dept, status, user } = ctx;
  const dispatch = useDispatch();
  const lists = useSelector(selectFocusLists);
  const overrides = useSelector(selectFocusOverrides);
  const [drawer, setDrawer] = useState<DrawerState>(null);
  const [disableTarget, setDisableTarget] = useState<FocusArea | null>(null);

  const key = `${org}:${dept}`;
  const scope = `${org} ${dept}`;
  const list = lists[key] ?? [];
  // Every location in appData.locations (same list as the drawer's Location dropdown).
  const scopeLocations = useAppLocationNames().map((name) => ({ name }));
  const overrideRows = scopeLocations.filter(
    (l) => (overrides[locationKey(l.name, dept)]?.off ?? []).length > 0
  );
  const nameOf = (id: string) => list.find((f) => f.id === id)?.name ?? id;

  const applyToggle = (f: FocusArea) =>
    ctx.guard(async () => {
      try {
        const updated = await updateFocusArea(f.id, {
          org,
          department: dept,
          enabled: !f.enabled,
          updatedBy: user,
        });
        // Replace only this item with what the backend returned.
        dispatch(
          setFocusLists({ ...lists, [key]: list.map((x) => (x.id === updated.id ? toFocusArea(updated) : x)) })
        );
        ctx.log([
          {
            section: 'Focus areas',
            setting: f.name,
            scope,
            prior: f.enabled ? 'Enabled' : 'Disabled',
            next: f.enabled ? 'Disabled' : 'Enabled',
            effective: 'Immediately',
          },
        ]);
      } catch (err) {
        console.error('[FocusAreasTab] Failed to toggle focus area:', err);
        dispatch(setSaveError(getApiErrorMessage(err)));
      }
      setDisableTarget(null);
    });

  // Disabling a focus area used by open coaching items asks for confirmation first.
  const toggle = (f: FocusArea) => (f.enabled && f.open > 0 ? setDisableTarget(f) : applyToggle(f));

  const saveName = (name: string) =>
    ctx.guard(async () => {
      try {
        if (drawer?.kind === 'name' && drawer.focus) {
          const editing = drawer.focus;
          const updated = await updateFocusArea(editing.id, {
            org,
            department: dept,
            name,
            updatedBy: user,
          });
          dispatch(
            setFocusLists({ ...lists, [key]: list.map((x) => (x.id === updated.id ? toFocusArea(updated) : x)) })
          );
          ctx.log([
            {
              section: 'Focus areas',
              setting: 'Focus area name',
              scope,
              prior: editing.name,
              next: updated.name,
              effective: 'Immediately',
            },
          ]);
        } else {
          // New focus area — POST to backend, insert returned item above "Other".
          const created = await createFocusArea({ org, department: dept, name, updatedBy: user });
          const other = list.find((x) => x.locked);
          const newList = [...list.filter((x) => !x.locked), toFocusArea(created), ...(other ? [other] : [])];
          dispatch(setFocusLists({ ...lists, [key]: newList }));
          ctx.log([
            { section: 'Focus areas', setting: name, scope, prior: 'None', next: 'Added', effective: 'Immediately' },
          ]);
        }
        setDrawer(null);
      } catch (err) {
        console.error('[FocusAreasTab] Failed to save focus area name:', err);
        dispatch(setSaveError(getApiErrorMessage(err)));
      }
    });

  const saveOverride = ({ loc, off, initial }: { loc: string; off: string[]; initial: string[] }) =>
    ctx.guard(async () => {
      try {
        if (off.length === 0) {
          // Empty override list — remove the override entirely.
          await deleteFocusAreaOverride(org, dept, loc);
          const next = { ...overrides };
          delete next[locationKey(loc, dept)];
          dispatch(setFocusOverrides(next));
        } else {
          const updated = await saveFocusAreaOverride({
            org,
            department: dept,
            location: loc,
            turnedOffIds: off,
            updatedBy: user,
          });
          // The response is the whole scope's focus config, so it replaces lists and overrides.
          const saved = focusToRedux(updated, lists);
          dispatch(setFocusLists(saved.lists));
          dispatch(setFocusOverrides(saved.overrides));
        }
        ctx.log([
          {
            section: 'Focus areas',
            setting: 'Turned off at location',
            scope: `${loc} / ${dept}`,
            prior: initial.map(nameOf).join(', ') || 'None',
            next: off.map(nameOf).join(', ') || 'None',
            effective: 'Immediately',
          },
        ]);
        setDrawer(null);
      } catch (err) {
        console.error('[FocusAreasTab] Failed to save focus area override:', err);
        dispatch(setSaveError(getApiErrorMessage(err)));
      }
    });

  return (
    <TabBody>
      <OrgDeptPicker />

      <ConfigCard
        title="National Focus Areas"
        subtitle={`For ${org} ${dept} coaching that does not come from an observation behavior, at every location.`}
        status={status}
        onRetry={ctx.retry}
        table
        action={
          <AdminButton
            label="Add Focus Area"
            small
            icon={<MaterialIcons name="add" size={16} color={AdminColors.textOnDark} />}
            disabled={status === 'loading'}
            onPress={() => setDrawer({ kind: 'name', focus: null })}
          />
        }
      >
        <ConfigTable
          columns={[
            { key: 'n', header: 'Focus area' },
            { key: 's', header: 'Status' },
            { key: 'a', header: 'Actions', hideHeader: true, flex: 0.8 },
          ]}
          rows={list.map((f) => ({
            key: f.id,
            cells: [
              <Text key="n" style={[styles.strong, !f.enabled && styles.dim]}>{f.name}</Text>,
              <AdminStatusBadge key="s" status={f.enabled ? 'active' : 'disabled'} label={f.enabled ? 'Enabled' : 'Disabled'} />,
              <View key="a" style={styles.actions}>
                <ConfigGhostButton label="Edit" onPress={() => setDrawer({ kind: 'name', focus: f })} />
                {!f.locked ? (
                  <ConfigGhostButton label={f.enabled ? 'Disable' : 'Enable'} onPress={() => toggle(f)} />
                ) : null}
              </View>,
            ],
          }))}
        />
      </ConfigCard>

      <ConfigCard
        title="Location Overrides"
        subtitle={`Optional. Turn specific national focus areas off for ${dept} at a location.`}
        status={status}
        onRetry={ctx.retry}
        table
        action={
          <AdminButton
            label="Add Override"
            variant="secondary"
            small
            disabled={status === 'loading' || scopeLocations.length === 0}
            onPress={() => setDrawer({ kind: 'override' })}
          />
        }
      >
        <ConfigTable
          columns={[
            { key: 'l', header: 'Location' },
            { key: 'o', header: 'Turned off', flex: 2, align: 'left' },
            { key: 'a', header: 'Actions', hideHeader: true, flex: 0.5 },
          ]}
          emptyMessage={
            scopeLocations.length
              ? 'No location overrides. Every location uses the national focus areas.'
              : 'No locations are available.'
          }
          rows={overrideRows.map((l) => ({
            key: l.name,
            cells: [
              <Text key="l" style={styles.strong}>{l.name}</Text>,
              overrides[locationKey(l.name, dept)].off.map(nameOf).join(', '),
              <ConfigGhostButton key="e" label="Edit" onPress={() => setDrawer({ kind: 'override', loc: l.name })} />,
            ],
          }))}
        />
      </ConfigCard>

      {drawer?.kind === 'name' ? (
        <ConfigNameDrawer
          key={`${key}:${drawer.focus?.id ?? 'new'}`}
          title={drawer.focus ? 'Edit focus area' : 'Add focus area'}
          subtitle={drawer.focus ? `${scope} / ${drawer.focus.name}` : `${scope}. Added focus areas start enabled`}
          label="Focus area name"
          initial={drawer.focus?.name}
          onClose={() => setDrawer(null)}
          onSave={saveName}
        />
      ) : null}

      {drawer?.kind === 'override' ? (
        <FocusOverrideDrawer
          key={`${key}:${drawer.loc ?? 'new'}`}
          org={org}
          dept={dept}
          list={list}
          startLoc={drawer.loc}
          overrides={overrides}
          onClose={() => setDrawer(null)}
          onSave={saveOverride}
        />
      ) : null}

      {disableTarget ? (
        <ConfigConfirmModal
          title={`Disable ${disableTarget.name}?`}
          message={`This focus area is used by ${disableTarget.open} open coaching items. Disable it to stop new use; existing items are not affected.`}
          confirmLabel="Disable"
          onCancel={() => setDisableTarget(null)}
          onConfirm={() => applyToggle(disableTarget)}
        />
      ) : null}
    </TabBody>
  );
};

const styles = StyleSheet.create({
  strong: { fontSize: 14, fontFamily: FontFamily.bold, color: AdminColors.textPrimary },
  dim: { color: AdminColors.textMuted },
  actions: { flexDirection: 'row', gap: 4 },
});

export default FocusAreasTab;
